<template>
    <div class="multi-select-container">
        <!-- Champ caché qui contiendra les valeurs pour la soumission du formulaire -->
        <input v-for="selectedValue in selectedValues" :key="selectedValue" type="hidden" :name="name" :value="selectedValue" />

        <!-- L'interface visible pour l'utilisateur -->
        <div class="form-control selected-items-wrapper" @click="focusSelect">
            <!-- Les "tags" pour les éléments sélectionnés -->
            <span v-for="item in selectedItems" :key="item.value" class="selected-item-tag">
                {{ item.label }}
                <button type="button" class="remove-btn" @click.stop="deselect(item.value)">&times;</button>
            </span>

            <!-- Le champ de recherche -->
            <input
                ref="selectInput"
                type="text"
                class="search-input"
                :placeholder="placeholderText"
                v-model="searchTerm"
                @focus="showDropdown = true"
                @keydown.delete="removeLastItem"
            />

            <!-- NOUVEAU : Bouton "Tout effacer" -->
            <button
                v-if="selectedValues.length > 0"
                type="button"
                class="clear-all-btn"
                @click.stop="clearAll"
                title="Tout effacer"
            >
                &times;
            </button>
        </div>

        <!-- La liste déroulante des options -->
        <ul v-if="showDropdown" class="dropdown-list">
            <li v-if="filteredOptions.length === 0" class="no-results">Aucun résultat</li>
            <li
                v-for="option in filteredOptions"
                :key="option.value"
                class="dropdown-item"
                @mousedown.prevent="select(option.value)"
            >
                {{ option.label }}
            </li>
        </ul>
    </div>
</template>

<script>
export default {
    props: {
        name: {
            type: String,
            required: true,
        },
        options: {
            type: Array,
            required: true,
        },
        initialSelected: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: 'Choisissez...',
        },
    },
    data() {
        return {
            selectedValues: [...this.initialSelected],
            searchTerm: '',
            showDropdown: false,
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('add-category-option', this.handleExternalAdd)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('add-category-option', this.handleExternalAdd)
    },
    computed: {
        selectedItems() {
            return this.options.filter((opt) => this.selectedValues.includes(opt.value))
        },
        availableOptions() {
            return this.options.filter((opt) => !this.selectedValues.includes(opt.value))
        },
        filteredOptions() {
            if (!this.searchTerm) {
                return this.availableOptions
            }
            const lowerSearchTerm = this.searchTerm.toLowerCase()
            return this.availableOptions.filter((opt) => opt.label.toLowerCase().includes(lowerSearchTerm))
        },
        placeholderText() {
            return this.selectedItems.length > 0 ? '' : this.placeholder
        },
    },
    methods: {
        select(value) {
            if (!this.selectedValues.includes(value)) {
                this.selectedValues.push(value)
            }
            this.searchTerm = ''
        },
        deselect(value) {
            this.selectedValues = this.selectedValues.filter((v) => v !== value)
        },
        // NOUVEAU : Méthode pour tout effacer
        clearAll() {
            this.selectedValues = []
            this.searchTerm = ''
            this.$refs.selectInput.focus() // On garde le focus sur l'input
        },
        removeLastItem() {
            if (this.searchTerm.length === 0 && this.selectedValues.length > 0) {
                this.selectedValues.pop()
            }
        },
        focusSelect() {
            this.$refs.selectInput.focus()
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.showDropdown = false
            }
        },
        handleExternalAdd(event) {
            const { value, label } = event.detail
            // Check if option already exists
            const existingIdx = this.options.findIndex((opt) => opt.value == value)
            if (existingIdx === -1) {
                this.options.push({ value, label })
            }
            // Select the value
            this.select(value)
        },
    },
}
</script>

<style scoped>
.multi-select-container {
    position: relative;
}

.selected-items-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    min-height: 56px;
    cursor: text;
    padding-right: 30px;
    /* Espace pour éviter que le texte ne passe sous le bouton clear si l'input est plein */
}

.selected-item-tag {
    display: inline-flex;
    align-items: center;
    background-color: #e9ecef;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 0.875rem;
}

.remove-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    line-height: 1;
    margin-left: 5px;
    cursor: pointer;
    color: #6c757d;
}

.search-input {
    border: none;
    outline: none;
    flex-grow: 1;
    padding: 5px;
    min-width: 50px;
    /* Pour s'assurer que l'input reste saisissable */
}

/* NOUVEAU : Style pour le bouton Tout effacer */
.clear-all-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    color: #dc3545;
    /* Rouge pour indiquer une suppression */
    padding: 0 10px;
    margin-left: auto;
    /* Pousse le bouton vers la droite */
}

.clear-all-btn:hover {
    color: #a71d2a;
}

.dropdown-list {
    position: absolute;
    top: 100%;
    left: 15px;
    right: 15px;
    background-color: white;
    border: 1px solid #ced4da;
    border-top: none;
    border-radius: 0 0 4px 4px;
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
}

.dropdown-item {
    padding: 8px 12px;
    cursor: pointer;
}

.dropdown-item:hover {
    background-color: #f8f9fa;
}

.no-results {
    padding: 8px 12px;
    color: #6c757d;
}
</style>
